// 장바구니 화면.
// 작업계획서 4.5: 상품/수량/금액 합계 표시, 수량 변경/삭제. 비로그인 사용자도 담을 수 있게 하고,
//   주문 확정 단계(Phase 3-C) 에서 로그인을 요구한다.
// 본 라운드(3-A)에서는 주문 버튼은 자리만 만들어 두고 다음 라운드에서 활성화한다.
//
// 손님 카피 톤([[feedback-customer-copy-tone]]): 부정/판단적 표현 회피.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { ORDER_TYPE, formatCartLineLabel, formatWeightG } from "@oo/shared";
import type { CartItemDto, OrderType } from "@oo/shared";
import { useCart } from "@/features/cart/useCart.ts";
import { imageUrl } from "@/features/catalog/imageUrl.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { Button } from "@/shared/ui";
import StoreStatusCard from "@/features/operations/StoreStatusCard.tsx";

// 장바구니 화면.
export default function CartPage() {
  const navigate = useNavigate();
  const cart = useCart();
  const ops = useStoreSettings();
  // 주문 방식(포장/배달) 선택 모달.
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const deliveryEnabled = ops.settings?.deliveryEnabled ?? false;
  const nextDayDeliveryOnly = ops.settings?.nextDayDeliveryOnly ?? false;

  // 주문하기 진입: 주문 방식 선택 모달을 띄운다. /checkout 은 ProtectedRoute 라 비로그인은 로그인 화면으로 보내진다.
  function onCheckout() {
    setTypeModalOpen(true);
  }
  // 방식을 고르면 그 방식을 고정해 체크아웃으로 보낸다(체크아웃에선 토글 없이 표시만).
  //  - 사용 불가/품절 항목 차단은 체크아웃 화면에서 한 번 더 안내하고, 서버도 최종 재검증한다.
  function startCheckout(orderType: OrderType) {
    setTypeModalOpen(false);
    navigate("/checkout", { state: { orderType } });
  }

  if (cart.isLoading) {
    return (
      <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
        <h1 className="text-2xl font-bold">장바구니</h1>
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      </main>
    );
  }

  const view = cart.view;
  const isEmpty = !view || view.items.length === 0;
  // 장바구니 전체 총 중량(g). 각 줄의 기본중량*수량 + 옵션중량 합계를 더한 값.
  const cartTotalWeightG = (view?.items ?? []).reduce((sum, it) => sum + (it.totalWeightG ?? 0), 0);

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-[calc(8rem+var(--safe-bottom))]">
      <header className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">장바구니</h1>
        <div className="flex items-center gap-3">
          {/* 주문내역 화면 바로가기(작업지시). 비로그인 시 ProtectedRoute 가 로그인으로 유도한다. */}
          <Link
            to="/me/orders"
            className="inline-flex min-h-touch items-center rounded-md border border-ink-300 px-3 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            주문내역 보기
          </Link>
          {view ? (
            <span className="shrink-0 text-sm text-ink-600">총 {view.itemCount}종</span>
          ) : null}
        </div>
      </header>

      {/* 영업 안내 카드 */}
      {ops.settings && ops.openStatus ? (
        <StoreStatusCard
          settings={ops.settings}
          openStatus={ops.openStatus}
          hasReservation={ops.reservationDays.length > 0}
        />
      ) : null}

      {isEmpty ? (
        <EmptyCart />
      ) : (
        <>
          {/* 상태 알림 영역 - 사용 불가/품절 항목이 있을 때만 표시 */}
          {view.unavailableCount > 0 ? (
            <NoticeBox
              tone="warning"
              title="확인이 필요한 상품이 있어요"
              body={`${view.unavailableCount}개 항목이 더 이상 판매되지 않습니다. 주문 전에 삭제해 주세요.`}
            />
          ) : null}
          {view.soldOutCount > 0 ? (
            <NoticeBox
              tone="info"
              title="품절된 상품이 있어요"
              body={`${view.soldOutCount}개 항목이 일시 품절입니다. 합계에서 제외되며, 다시 들어오면 그대로 담아두실 수 있어요.`}
            />
          ) : null}

          <ul className="flex flex-col gap-2">
            {view.items.map((it) => (
              <CartLineRow key={it.id} item={it} />
            ))}
          </ul>

          <section className="rounded-lg border border-ink-200 bg-ink-50 p-4">
            {/* 총 중량(기본+추가옵션 합산). 중량을 알 수 없는 상품만 담겼으면 표시하지 않는다. */}
            {cartTotalWeightG > 0 ? (
              <div className="mb-1 flex items-baseline justify-between text-sm">
                <span className="text-ink-700">총 중량</span>
                <span className="font-medium tabular-nums text-ink-800">
                  {formatWeightG(cartTotalWeightG)}
                </span>
              </div>
            ) : null}
            <div className="flex items-baseline justify-between">
              <span className="text-base text-ink-700">합계</span>
              <span className="text-2xl font-bold tabular-nums">
                {view.totalKrw.toLocaleString()}원
              </span>
            </div>
          </section>
        </>
      )}

      {/* 하단 고정 주문 버튼. 빈 카트/사용 불가/품절 항목이 있으면 비활성. */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+var(--safe-bottom))] z-10 border-t border-ink-200 bg-white px-4 py-3">
        <div className="mx-auto max-w-screen-sm">
          <Button
            type="button"
            size="lg"
            fullWidth
            onClick={onCheckout}
            disabled={isEmpty || (!!view && view.unavailableCount > 0)}
          >
            주문하기
          </Button>
        </div>
      </div>

      {/* 주문 방식 선택 모달: 포장/배달 큰 버튼 2개. 선택 즉시 체크아웃으로(방식 고정). */}
      {typeModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/50 p-0 sm:items-center sm:p-4"
          onClick={() => setTypeModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-type-title"
            className="w-full max-w-screen-sm rounded-t-2xl bg-surface p-5 pb-[calc(1.25rem+var(--safe-bottom))] shadow-elevated sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="order-type-title" className="text-lg font-bold text-ink-900">
              어떻게 받으시겠어요?
            </h2>
            <p className="mt-1 text-sm text-ink-600">주문 방식을 선택해 주세요.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => startCheckout(ORDER_TYPE.PICKUP)}
                className="flex flex-col items-start rounded-xl border-2 border-ink-200 bg-white p-5 text-left hover:border-brand-600 hover:bg-brand-50"
              >
                <span className="text-lg font-bold text-ink-900">포장</span>
                <span className="mt-1 text-sm text-ink-600">매장에서 수령</span>
              </button>
              <button
                type="button"
                disabled={!deliveryEnabled}
                onClick={() => deliveryEnabled && startCheckout(ORDER_TYPE.DELIVERY)}
                className="flex flex-col items-start rounded-xl border-2 border-ink-200 bg-white p-5 text-left hover:border-brand-600 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-ink-200 disabled:hover:bg-white"
              >
                <span className="text-lg font-bold text-ink-900">
                  {nextDayDeliveryOnly ? "내일 배달" : "배달"}
                </span>
                <span className="mt-1 text-sm text-ink-600">
                  {deliveryEnabled
                    ? nextDayDeliveryOnly
                      ? "내일 오전 배송"
                      : "배송지로 수령"
                    : "현재 준비중"}
                </span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setTypeModalOpen(false)}
              className="mt-4 h-11 w-full rounded-md border border-ink-300 text-sm font-semibold text-ink-700 hover:bg-ink-50"
            >
              닫기
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

// 빈 카트 안내.
function EmptyCart() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-ink-300 p-10 text-center">
      <p className="text-base font-medium">담긴 상품이 없습니다.</p>
      <p className="text-sm text-ink-600">메인 또는 카테고리에서 상품을 담아 보세요.</p>
      <Link
        to="/category"
        className="mt-2 inline-flex min-h-touch items-center justify-center rounded bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
      >
        카테고리 보기
      </Link>
    </div>
  );
}

// 안내 박스 (warning/info).
function NoticeBox({
  tone,
  title,
  body,
}: {
  tone: "warning" | "info";
  title: string;
  body: string;
}) {
  const className =
    tone === "warning"
      ? "border-warning-600/30 bg-warning-50 text-warning-700"
      : "border-sky-300 bg-sky-50 text-sky-900";
  return (
    <div className={`rounded-md border p-3 text-sm ${className}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-0.5 text-xs">{body}</p>
    </div>
  );
}

// 카트 한 줄.
// 수량 +/-/삭제는 useCart 가 노출한 동작에 위임한다 (로그인/비로그인 동일 인터페이스).
function CartLineRow({ item }: { item: CartItemDto }) {
  const cart = useCart();
  const units = useUnits();
  const s = item.productSnapshot;
  const url = imageUrl(s.mainImageKey);
  const disabled = !s.isAvailable || s.isSoldOut;

  // 수량/삭제는 즉시 화면 반영(낙관적)되고 서버 동기화는 useCart 가 디바운스/정합 처리한다.
  // 버튼을 막지 않으므로 연타해도 바로바로 반응한다.
  function onMinus() {
    if (disabled) return;
    if (item.quantity <= 1) {
      // 1 에서 - 누르면 삭제 동작.
      cart.removeItem({ id: item.id, productId: item.productId });
      return;
    }
    cart.changeQuantity({ id: item.id, productId: item.productId }, -1);
  }

  function onPlus() {
    if (disabled) return;
    cart.changeQuantity({ id: item.id, productId: item.productId }, 1);
  }

  function onRemove() {
    cart.removeItem({ id: item.id, productId: item.productId });
  }

  return (
    <li
      className={`flex gap-3 rounded-lg border p-3 ${
        disabled ? "border-ink-200 bg-ink-50" : "border-ink-200 bg-white"
      }`}
    >
      <div
        className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-ink-100 ${
          disabled ? "opacity-60" : ""
        }`}
      >
        {url ? (
          <img src={url} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[11px] text-ink-600">
            이미지 없음
          </div>
        )}
        {s.isSoldOut ? (
          <span className="absolute left-1 top-1 rounded bg-ink-900/80 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            품절
          </span>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`line-clamp-1 text-sm font-medium ${disabled ? "text-ink-600" : "text-ink-900"}`}>
            {s.name}
          </p>
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 rounded px-2 py-0.5 text-xs text-ink-600 hover:bg-ink-100"
            aria-label="삭제"
          >
            삭제
          </button>
        </div>

        <p className="text-xs text-ink-600">
          {formatCartLineLabel(units, s.unit, item.quantity)}
        </p>

        {/* 추가 옵션 목록 + 총 중량(기본+추가). 금액은 아래 줄 합계에 이미 포함돼 있다. */}
        {item.options.length > 0 ? (
          <ul className="mt-0.5 flex flex-col gap-0.5">
            {item.options.map((o) => (
              <li key={o.optionId} className="text-xs text-ink-600">
                + {o.label} x{o.quantity}
                <span className="ml-1 text-ink-500">
                  ({(o.addPriceKrw * o.quantity).toLocaleString()}원
                  {o.addGrams > 0 ? ` · ${formatWeightG(o.addGrams * o.quantity)}` : ""})
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {item.totalWeightG ? (
          <p className="mt-0.5 text-xs font-medium text-brand-700">
            총 중량 {formatWeightG(item.totalWeightG)}
          </p>
        ) : null}

        <div className="mt-1 flex items-center justify-between gap-2">
          {/* 수량 조절. 사용 불가/품절은 잠금. */}
          <div className="flex items-center rounded-md border border-ink-300">
            <button
              type="button"
              onClick={onMinus}
              disabled={disabled}
              className="inline-flex min-h-touch min-w-touch items-center justify-center text-ink-700 disabled:opacity-50"
              aria-label="수량 감소"
            >
              <Minus size={18} aria-hidden="true" />
            </button>
            <span className="min-w-[2rem] text-center text-sm tabular-nums">{item.quantity}</span>
            <button
              type="button"
              onClick={onPlus}
              disabled={disabled}
              className="inline-flex min-h-touch min-w-touch items-center justify-center text-ink-700 disabled:opacity-50"
              aria-label="수량 증가"
            >
              <Plus size={18} aria-hidden="true" />
            </button>
          </div>
          <span className={`text-base font-bold tabular-nums ${disabled ? "text-ink-400" : "text-ink-900"}`}>
            {item.lineTotalKrw.toLocaleString()}원
          </span>
        </div>
      </div>
    </li>
  );
}
